import { createStore } from '@/common/pinia';
import type { ApiResponse } from '@/common/services/api.service-new';
import type {
  LulcBiomassDensityResponse,
  LulcChangeResponse,
  LulcClassGroup,
  LulcLayerResponse,
  LulcTimeseriesResponse,
} from './lulc.types';

class LulcStore {
  /** Tile-URL responses keyed by year (current=2025, 2020, ...). */
  private layers: Map<number, ApiResponse<LulcLayerResponse>> = new Map();

  /** Per-polygon stats keyed by `${year}|${fingerprint}`. */
  private statsCache: Map<string, LulcClassGroup[]> = new Map();

  /** Per-polygon biomass-density cache (current LULC only). */
  private densityCache: Map<string, LulcBiomassDensityResponse> = new Map();

  /** Full configured yearly distribution, keyed by AOI fingerprint. */
  private timeseriesCache: Map<string, LulcTimeseriesResponse> = new Map();

  /** Detailed/all-axis pairwise change, keyed by years + AOI fingerprint. */
  private changeCache: Map<string, LulcChangeResponse> = new Map();

  public getLayer(year: number): ApiResponse<LulcLayerResponse> | null {
    return this.layers.get(year) ?? null;
  }

  public setLayer(year: number, layer: ApiResponse<LulcLayerResponse>): void {
    this.layers.set(year, layer);
  }

  public getStatsCache(year: number, fingerprint: string): LulcClassGroup[] | undefined {
    return this.statsCache.get(LulcStore.statsKey(year, fingerprint));
  }

  public setStatsCache(year: number, fingerprint: string, groups: LulcClassGroup[]): void {
    LulcStore.setBounded(this.statsCache, LulcStore.statsKey(year, fingerprint), groups);
  }

  public getDensityCache(fingerprint: string): LulcBiomassDensityResponse | undefined {
    return this.densityCache.get(fingerprint);
  }

  public setDensityCache(fingerprint: string, response: LulcBiomassDensityResponse): void {
    LulcStore.setBounded(this.densityCache, fingerprint, response);
  }

  public getTimeseriesCache(fingerprint: string): LulcTimeseriesResponse | undefined {
    return this.timeseriesCache.get(fingerprint);
  }

  public setTimeseriesCache(fingerprint: string, timeseries: LulcTimeseriesResponse): void {
    LulcStore.setBounded(this.timeseriesCache, fingerprint, timeseries);
  }

  public getChangeCache(from: number, to: number, fingerprint: string): LulcChangeResponse | undefined {
    return this.changeCache.get(LulcStore.changeKey(from, to, fingerprint));
  }

  public setChangeCache(from: number, to: number, fingerprint: string, change: LulcChangeResponse): void {
    LulcStore.setBounded(this.changeCache, LulcStore.changeKey(from, to, fingerprint), change);
  }

  /**
   * AOI cache keys embed the full geometry, so cap them and evict the oldest entry.
   * Re-inserting keeps the most recently written value at the end of the Map.
   */
  private static readonly MAX_AOI_CACHE_ENTRIES = 64;

  private static setBounded<V>(cache: Map<string, V>, key: string, value: V): void {
    cache.delete(key);
    cache.set(key, value);
    if (cache.size > LulcStore.MAX_AOI_CACHE_ENTRIES) {
      const oldest = cache.keys().next().value;
      if (oldest !== undefined) {
        cache.delete(oldest);
      }
    }
  }

  private static statsKey(year: number, fingerprint: string): string {
    return `${year}|${fingerprint}`;
  }

  private static changeKey(from: number, to: number, fingerprint: string): string {
    return `${from}|${to}|detailed|all|${fingerprint}`;
  }
}

const useLulcStore = createStore(LulcStore);

export { useLulcStore, type LulcStore };
